/**
 * Voice AI — Shared Settings Styles
 * Common CSS for all settings sub-components.
 * Mirrors the patterns used by llm-settings.js and stt-tts-settings.js.
 */

import { css } from 'lit';

export const settingsStyles = css`
  :host { display: block; }

  /* ─── Content Header ─── */
  .content-header { margin-bottom: var(--space-6); }
  .content-title {
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
    margin-bottom: var(--space-1);
  }
  .content-description {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    line-height: var(--leading-relaxed);
  }

  /* ─── Settings Section (glass card) ─── */
  .settings-section {
    margin-bottom: var(--space-8);
    background: rgba(255, 255, 255, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06),
                inset 0 1px 0 rgba(255, 255, 255, 0.5),
                inset 0 -1px 0 rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(8px) saturate(1.2);
    -webkit-backdrop-filter: blur(8px) saturate(1.2);
    position: relative;
    overflow: hidden;
  }

  /* ─── Section Title Row ─── */
  .section-title-row {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-1);
  }
  .section-icon {
    color: var(--color-primary);
    font-size: var(--text-base);
  }
  .section-title {
    font-family: var(--font-display);
    font-size: var(--text-base);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
  }
  .section-description {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    margin-bottom: var(--space-4);
  }

  /* Form base styles inherited from componentStyles (design-system.js) */

  /* ─── Password Fields ─── */
  .password-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .password-wrap .form-input {
    padding-right: var(--space-10);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    letter-spacing: 0.05em;
  }
  .password-toggle {
    position: absolute;
    right: var(--space-2);
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: var(--color-text-tertiary);
    cursor: pointer;
    padding: var(--space-1);
    border-radius: var(--radius-sm);
    transition: color var(--transition-fast);
    font-size: var(--text-sm);
  }
  .password-toggle:hover { color: var(--color-text-primary); }

  /* ─── Toggle Switch ─── */
  .toggle {
    position: relative;
    width: 40px;
    height: 22px;
    background: var(--color-border-secondary);
    border-radius: var(--radius-full);
    cursor: pointer;
    transition: background var(--transition-fast);
    flex-shrink: 0;
  }
  .toggle.active {
    background: var(--color-primary);
  }
  .toggle::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background: white;
    border-radius: var(--radius-full);
    box-shadow: var(--shadow-sm);
    transition: transform var(--transition-fast);
  }
  .toggle.active::after {
    transform: translateX(18px);
  }

  /* ─── Feature List (toggle rows) ─── */
  .feature-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .feature-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-4);
    background: var(--color-bg-tertiary);
    border-radius: var(--radius-xl);
    border: 1px solid var(--color-border-subtle);
    transition: all var(--transition-base);
  }
  .feature-item:hover {
    border-color: var(--color-border-primary);
    box-shadow: var(--shadow-sm);
  }
  .feature-info {
    flex: 1;
    min-width: 0;
  }
  .feature-label {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-primary);
    margin-bottom: 2px;
    display: flex;
    align-items: center;
  }
  .feature-desc {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
    line-height: var(--leading-relaxed);
  }

  /* ─── Redaction Grid ─── */
  .redaction-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-3);
  }
  @media (max-width: 900px) {
    .redaction-grid { grid-template-columns: repeat(2, 1fr); }
  }
  .redaction-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--color-bg-tertiary);
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border-subtle);
    transition: all var(--transition-base);
  }
  .redaction-item:hover {
    border-color: var(--color-border-primary);
    box-shadow: var(--shadow-sm);
  }
  .redaction-label {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-primary);
  }

  /* ─── Connection Status ─── */
  .connection-status {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    display: flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--color-text-tertiary);
    transition: color var(--transition-fast);
  }
  .connection-status.success {
    color: var(--color-success);
  }
  .connection-status.error {
    color: var(--color-error);
  }

  /* ─── Webhook Items ─── */
  .webhook-item {
    padding: var(--space-4);
    background: var(--color-bg-tertiary);
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border-subtle);
    margin-bottom: var(--space-3);
  }
  .webhook-header {
    display: flex;
    align-items: flex-start;
    gap: var(--space-3);
    margin-bottom: var(--space-3);
  }

  /* ─── Checkbox Group ─── */
  .checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
  }
  .checkbox-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    cursor: pointer;
  }
  .checkbox-item input[type="checkbox"] {
    accent-color: var(--color-primary);
  }

  /* ─── Table Wrap ─── */
  .table-wrap {
    overflow-x: auto;
    border-radius: var(--radius-lg);
    border: 1px solid var(--color-border-subtle);
  }

  /* ─── Actions Bar ─── */
  .actions-bar {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
    padding-top: var(--space-4);
    border-top: 1px solid var(--color-border-primary);
    margin-top: var(--space-2);
  }

  /* Buttons — base styles from componentStyles (design-system.js)
     Settings-specific overrides only below */

  /* ─── Data Table ─── */
  .data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-family: var(--font-body);
  }
  .data-table thead th {
    padding: var(--space-3) var(--space-5);
    text-align: left;
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
    border-bottom: 2px solid var(--color-border-primary);
    background: var(--color-bg-tertiary);
    white-space: nowrap;
  }
  .data-table tbody td {
    padding: var(--space-4) var(--space-5);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    border-bottom: 1px solid var(--color-border-subtle);
    vertical-align: middle;
  }
  .data-table tbody tr {
    transition: all var(--transition-base);
  }
  .data-table tbody tr:hover {
    background: var(--color-bg-hover);
    box-shadow: inset 3px 0 0 var(--color-accent);
  }
  .data-table tbody tr:last-child td {
    border-bottom: none;
  }

  /* ─── Provider List (LLM / STT / TTS) ─── */
  .provider-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .provider-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-4);
    background: var(--color-bg-tertiary);
    border-radius: var(--radius-xl);
    border: 1px solid var(--color-border-subtle);
    transition: all var(--transition-base);
    cursor: pointer;
  }
  .provider-item:hover {
    border-color: var(--color-border-primary);
    transform: scale(1.003);
    box-shadow: var(--shadow-sm);
  }
  .provider-item.active {
    border-color: var(--color-primary);
    box-shadow: var(--shadow-glow-primary);
  }
  .provider-info {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    flex: 1;
    min-width: 0;
  }
  .provider-icon {
    width: 38px;
    height: 38px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
    background: var(--color-primary-light);
    color: var(--color-primary);
    border: 1px solid var(--color-primary-subtle);
  }
  .provider-details {
    flex: 1;
    min-width: 0;
  }
  .provider-name {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-primary);
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .provider-meta {
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }
  .provider-right {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-shrink: 0;
  }
  .edit-icon {
    color: var(--color-text-tertiary);
    font-size: var(--text-xs);
    transition: color var(--transition-fast);
  }
  .provider-item:hover .edit-icon { color: var(--color-primary); }

  /* ─── Add Button (dashed) ─── */
  .add-btn {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    background: transparent;
    border: 1px dashed var(--color-border-secondary);
    border-radius: var(--radius-xl);
    cursor: pointer;
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-tertiary);
    transition: all var(--transition-base);
    justify-content: center;
    margin-top: var(--space-3);
  }
  .add-btn:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: var(--color-primary-light);
  }

  /* ─── Badges ─── */
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: var(--radius-full);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-bold);
    letter-spacing: 0.02em;
  }
  .badge-primary {
    background: var(--color-primary-subtle);
    color: var(--color-primary);
  }
  .badge-success {
    background: var(--color-success-bg);
    color: var(--color-success-text);
  }
  .badge-warning {
    background: var(--color-warning-bg);
    color: var(--color-warning-text);
  }

  /* ─── Slider Controls ─── */
  .slider-wrap {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .slider-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .slider-value {
    font-family: var(--font-mono);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    color: var(--color-primary);
    background: var(--color-primary-light);
    padding: 2px var(--space-2);
    border-radius: var(--radius-sm);
  }
  input[type="range"] {
    -webkit-appearance: none;
    width: 100%;
    height: 6px;
    background: var(--color-bg-inset);
    border-radius: var(--radius-full);
    outline: none;
  }
  input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border-radius: var(--radius-full);
    background: var(--color-primary);
    cursor: pointer;
    box-shadow: var(--shadow-sm);
    transition: all var(--transition-fast);
  }
  input[type="range"]::-webkit-slider-thumb:hover {
    transform: scale(1.15);
    box-shadow: var(--shadow-glow-primary);
  }

  /* ─── Form Actions (provider edit forms) ─── */
  .form-actions {
    display: flex;
    gap: var(--space-3);
    margin-top: var(--space-5);
    padding-top: var(--space-4);
    border-top: 1px solid var(--color-border-primary);
  }

  /* ─── Settings-specific button overrides ─── */
  /* Settings uses outlined danger buttons instead of solid */
  .btn-danger {
    background: transparent;
    color: var(--color-error);
    border-color: var(--color-error-bg);
  }
  .btn-danger:hover { background: var(--color-error-bg); }

  .spacer { flex: 1; }

  /* ─── Animations ─── */
  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* ─── Empty State ─── */
  .empty-state {
    text-align: center;
    padding: var(--space-8) var(--space-4);
    color: var(--color-text-tertiary);
  }
  .empty-state i {
    font-size: var(--text-3xl);
    margin-bottom: var(--space-3);
    display: block;
    opacity: 0.5;
  }
  .empty-hint {
    text-align: center;
    padding: var(--space-6);
    color: var(--color-text-tertiary);
    font-family: var(--font-body);
    font-size: var(--text-sm);
  }
  .empty-hint i {
    display: block;
    font-size: 1.5rem;
    margin-bottom: var(--space-2);
    opacity: 0.4;
  }

  /* Skeleton loading uses shared styles from design-system.js */

  /* Modal/dialog overlays now handled by <voice-modal> component */
`;
